import { Link } from 'react-router-dom';
import PublicLayout, { SEOHead, InternalLinks } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { SERVICES, INDUSTRIES, LANGUAGES } from '../../data/seo-data';
import { Phone, Video, Users, Mic, ArrowRight, CheckCircle, Shield, Zap, Building2, UserCheck, Lock, Eye, BarChart3, Globe, Scale, Landmark, Stethoscope, GraduationCap, Headphones, ChevronRight } from 'lucide-react';

const ICON_MAP = { Stethoscope, Scale, Landmark, Building2, GraduationCap, Shield, Headphones };

export default function HomePage() {
  return (
    <PublicLayout>
      <SEOHead
        title="Professional Interpreter Services | 24/7 Phone, Video & On-Site | Interpreter Services"
        description="The right interpreter, every time — AI or human interpreters. 250+ languages. Built for healthcare, legal, government, policing, immigration, and corrections."
        canonical="https://interpreterservices.us"
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Interpreter Services",
          description: "The right interpreter, every time — AI or human interpreters. Smart interpreting for enterprise environments",
          url: "https://interpreterservices.us",
          serviceType: "AI Interpreting Services",
          areaServed: ["United States", "Canada", "United Kingdom", "Australia"],
        }}
      />

      {/* SECTION 1: HERO */}
      <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-20 lg:py-28" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-1.5 text-sm text-blue-300 mb-6">
              <Zap className="w-3.5 h-3.5" /> Smart Interpreting Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              AI or Human Interpreters.<br />
              <span className="text-blue-400">You Choose.</span>
            </h1>
            <p className="text-lg text-slate-300 mb-4 max-w-lg leading-relaxed">
              The right interpreter, every time — AI or human interpreters. Instant AI. Expert humans. Total choice.
            </p>
            <p className="text-sm text-slate-400 mb-8 max-w-lg">
              AI when you want it. Human when you need it. Industry and function-specific solutions built for enterprise.
            </p>
            <div className="flex flex-wrap gap-4 mb-6">
              <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8" data-testid="hero-start-free" style={{display:'none'}}>Start Free</Button></a>
              <Link to="/book-demo"><Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 px-8" data-testid="hero-book-demo">Book Demo</Button></Link>
            </div>
            <Link to="/how-it-works" className="text-blue-400 text-sm font-medium hover:underline inline-flex items-center gap-1">How It Works <ChevronRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="hidden lg:block">
            <img src="/hero-team.png" alt="Interpreter Services enterprise interpreting platform for secure real-time voice interpretation" className="rounded-2xl shadow-2xl w-full" loading="eager" />
          </div>
        </div>
      </section>

      {/* SECTION 2: THREE CORE PILLARS */}
      <section className="py-20 bg-white" data-testid="pillars-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">How Interpreter Services Works</h2>
          <p className="text-slate-600 text-center mb-14 max-w-2xl mx-auto">Three layers of language access designed for high-stakes environments.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: 'Instant AI, On Demand', color: 'blue', items: ['Start interpreting in seconds', 'No scheduling required', 'Real-time audio-first communication'] },
              { icon: UserCheck, title: 'Human Escalation', tag: 'Premium', color: 'emerald', items: ['Professional human interpreters on demand', 'Seamless switch from AI to human', 'SLA-backed response for supported languages'] },
              { icon: Building2, title: 'Enterprise Ready', color: 'slate', items: ['Hosted AI architecture', 'Configurable controls', 'Reporting and audit capability'] },
            ].map(p => (
              <div key={p.title} className="border rounded-xl p-8 hover:shadow-lg transition-shadow" data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className={`w-12 h-12 rounded-lg bg-${p.color}-100 flex items-center justify-center mb-5`}>
                  <p.icon className={`w-6 h-6 text-${p.color}-700`} />
                </div>
                <h3 className="text-xl font-bold mb-1">{p.title} {p.tag && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-1">{p.tag}</span>}</h3>
                <ul className="mt-4 space-y-3">
                  {p.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: HOW IT WORKS */}
      <section className="py-20 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Start Interpreting in Three Steps</h2>
          <p className="text-slate-600 text-center mb-14 max-w-2xl mx-auto">Create an account for discounted rates or pay before each use.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Start an AI Interpreter Session', desc: 'Select the language and begin instantly. No scheduling, no wait times.' },
              { step: '2', title: 'Speak Naturally', desc: 'Use real-time audio interpretation for rapid communication across languages.' },
              { step: '3', title: 'Escalate If Needed', desc: 'Connect to a professional human interpreter in seconds when the situation requires it.' },
            ].map(s => (
              <div key={s.step} className="bg-white rounded-xl p-8 border hover:shadow-md transition-shadow text-center">
                <div className="w-12 h-12 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-5">{s.step}</div>
                <h3 className="text-lg font-bold mb-3">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/how-it-works"><Button variant="outline" className="text-blue-700 border-blue-700 hover:bg-blue-50">Learn More About How It Works <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      {/* SECTION 4: USE CASES / INDUSTRIES */}
      <section className="py-20 bg-white" data-testid="industries-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Built for High-Stakes Industries</h2>
          <p className="text-slate-600 text-center mb-14 max-w-2xl mx-auto">Interpreter Services provides specialized interpreting for industries where accurate communication is critical.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map(ind => {
              const examples = {
                'healthcare-interpreting-services': ['Patient intake', 'Telehealth visits', 'Discharge planning'],
                'legal-interpreting-services': ['Depositions', 'Court hearings', 'Attorney-client meetings'],
                'government-interpreting-services': ['Benefits administration', 'Public outreach', 'Emergency management'],
                'corporate-interpreting-services': ['Global meetings', 'HR onboarding', 'Training sessions'],
                'education-interpreting-services': ['IEP meetings', 'Parent conferences', 'Enrollment'],
                'policing-interpreting-services': ['Field interviews', 'Custody intake', 'Rapid frontline communication'],
                'immigration-interpreting-services': ['Intake interviews', 'Documentation clarification', 'Case communication'],
                'corrections-interpreting-services': ['Intake and processing', 'Safety communication', 'Administrative interactions'],
                'call-centers-interpreting-services': ['Customer support', 'Multilingual queues', 'Escalation handling'],
              };
              return (
                <Link key={ind.slug} to={`/industries/${ind.slug}`} className="group bg-white rounded-xl p-6 border hover:shadow-lg hover:border-blue-200 transition-all" data-testid={`industry-card-${ind.slug}`}>
                  <h3 className="font-bold text-lg mb-3 group-hover:text-blue-700">{ind.name}</h3>
                  <ul className="space-y-1.5 mb-4">
                    {(examples[ind.slug] || []).map(ex => (
                      <li key={ex} className="text-sm text-slate-600 flex items-center gap-2"><ChevronRight className="w-3 h-3 text-blue-500" />{ex}</li>
                    ))}
                  </ul>
                  <span className="text-blue-700 text-sm font-medium flex items-center gap-1">Learn More <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5: TRUST / SECURITY / PRIVACY */}
      <section className="py-20 bg-slate-900 text-white" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Built for Sensitive Environments</h2>
          <p className="text-slate-400 text-center mb-14 max-w-2xl mx-auto">Designed to support enterprise and government requirements in high-trust environments.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Lock, text: 'Hosted AI architecture' },
              { icon: Shield, text: 'No public model training on your data' },
              { icon: Eye, text: 'Configurable data retention' },
              { icon: BarChart3, text: 'Audit-ready session controls' },
              { icon: Globe, text: 'Jurisdiction-aware interpreter matching' },
              { icon: Building2, text: 'Enterprise policy controls' },
            ].map(t => (
              <div key={t.text} className="flex items-start gap-4 bg-slate-800/50 rounded-lg p-5">
                <t.icon className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-300">{t.text}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/security"><Button variant="outline" className="border-slate-500 text-white hover:bg-white/10">Learn About Our Security <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      {/* SECTION 6: AI TRANSPARENCY */}
      <section className="py-20 bg-white" data-testid="transparency-section">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Clear. Controlled. Transparent.</h2>
          <p className="text-slate-600 text-center mb-14 max-w-2xl mx-auto">You stay in control at every step of the interpreting session.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              'AI Interpreter is clearly identified in each session',
              'Human escalation is always available when needed',
              'Advisory warnings appear when accuracy may be reduced',
              'Users remain in control of escalation decisions',
            ].map(item => (
              <div key={item} className="flex items-start gap-3 p-5 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: PRICING PREVIEW */}
      <section className="py-20 bg-slate-50" data-testid="pricing-preview-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4">Start Free. Pay for Usage.</h2>
          <p className="text-slate-600 text-center mb-14 max-w-2xl mx-auto">Simple, transparent pricing that scales with your needs.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Free Tier', desc: 'Limited AI interpreting usage to get started. No credit card required.', cta: 'Start Free', link: '/auth?tab=register', highlight: false, hidden: true },
              { title: 'Usage-Based AI', desc: 'Pay per minute for AI interpreting. No contracts, no minimums.', cta: 'Get Started', link: '/auth?tab=register', highlight: true, hidden: true },
              { title: 'Human Escalation', desc: 'Premium access to professional human interpreters billed per session.', cta: 'Learn More', link: '/pricing', highlight: false },
            ].map(p => (
              <div key={p.title} className={`rounded-xl p-8 border ${p.highlight ? 'border-blue-600 bg-white shadow-lg ring-1 ring-blue-600' : 'bg-white'}`}>
                <h3 className="text-xl font-bold mb-3">{p.title}</h3>
                <p className="text-sm text-slate-600 mb-6">{p.desc}</p>
                <a href={p.link} target={p.link.includes('auth') ? '_blank' : undefined} rel={p.link.includes('auth') ? 'noopener noreferrer' : undefined}><Button className={p.highlight ? 'bg-blue-600 hover:bg-blue-700 text-white w-full' : 'w-full'} variant={p.highlight ? 'default' : 'outline'} style={p.hidden ? {display:'none'} : undefined}>{p.cta}</Button></a>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-6 text-center">
            <p className="text-sm text-slate-700 mb-3"><strong>Enterprise:</strong> Custom pricing available for organizations requiring SLA guarantees, policy controls, and dedicated setup.</p>
            <Link to="/contact"><Button variant="outline" className="text-blue-700 border-blue-700 hover:bg-blue-50">Talk to Sales</Button></Link>
          </div>
        </div>
      </section>

      {/* SECTION 8: NETWORK / SCALE */}
      <section className="py-20 bg-white" data-testid="network-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Powered by the Interpreter Services Global Interpreter Network</h2>
          <p className="text-slate-600 mb-10 max-w-2xl mx-auto">A global network of qualified interpreters and advanced AI, working together to deliver reliable language access.</p>
          <div className="grid sm:grid-cols-3 gap-8 text-left">
            {[
              { title: '250+ Languages', desc: 'AI interpreting across the world\'s most spoken and underserved languages.' },
              { title: 'Human Escalation', desc: 'Available when risk, policy, or complexity requires a professional human interpreter.' },
              { title: 'Local or Global', desc: 'Supply options configurable to your organization\'s requirements.' },
            ].map(n => (
              <div key={n.title} className="p-5 bg-slate-50 rounded-lg">
                <h4 className="font-bold mb-2">{n.title}</h4>
                <p className="text-sm text-slate-600">{n.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: COMPARISON / DIFFERENTIATION */}
      <section className="py-20 bg-slate-50" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-14">Traditional Interpreting vs Interpreter Services</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 border">
              <h3 className="text-lg font-bold mb-6 text-slate-500">Traditional Approach</h3>
              <ul className="space-y-4">
                {['Manual scheduling required', 'Slow to connect', 'Expensive for simple interactions', 'Limited transparency', 'Difficult to scale'].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-500"><span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5 text-xs text-slate-400">&minus;</span>{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-blue-600 rounded-xl p-8 text-white">
              <h3 className="text-lg font-bold mb-6">Interpreter Services</h3>
              <ul className="space-y-4">
                {['Instant AI interpreting on demand', 'Expert human interpreters when you need them', 'Faster connection times', 'Usage-based pricing model', 'Enterprise controls and audit', 'Total choice — AI or human interpreters, every session'].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm"><CheckCircle className="w-5 h-5 text-blue-200 shrink-0 mt-0.5" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10: FINAL CTA */}
      <section className="py-20 bg-slate-900 text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Language Access Without Delay</h2>
          <p className="text-slate-400 mb-10 max-w-xl mx-auto">Join organizations across healthcare, legal, government, and public safety that trust Interpreter Services for instant, reliable language access.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/auth?tab=register" target="_blank" rel="noopener noreferrer"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-10" data-testid="final-start-free" style={{display:'none'}}>Start Free</Button></a>
            <Link to="/book-demo"><Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 px-10" data-testid="final-book-demo">Book Demo</Button></Link>
            <Link to="/book-interpreter"><Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 px-10">Book Interpreter</Button></Link>
          </div>
        </div>
      </section>

      <InternalLinks
        cities={['New York', 'Los Angeles', 'London']}
        languages={LANGUAGES.slice(0, 3)}
        services={SERVICES}
      />
    </PublicLayout>
  );
}
